import React, { useState } from 'react';
import { Tooltip } from '@asigna/kit';
import { makeStyles, Theme } from '@material-ui/core';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { ContractCallBase, STXTransferBase } from '@stacks/connect';
import { deserializeTransaction } from '@stacks/transactions';
import { useBreakpoints } from 'hooks/use-breakpoints';
import arrowIcon from 'images/arrow.svg';
import { LinkIcon16 } from 'modules/icons/LinkIcon16';
import { getEventFromTransferMemo } from 'utils/stx20util';

import { STX_DECIMALS } from '../../constants/base';
import useNetwork from '../../hooks/use-network';
import { ReactComponent as InfoIcon } from '../../images/icon_small_info.svg';
import ReadOnlyIcon from '../../images/readonly_icon.svg';
import { TransactionStatus } from '../../store';
import { formatDate } from '../../utils/dateUtils';
import { getAccountExplorerUrl, getTxnExplorerUrl } from '../../utils/helper';
import { IMultisigTransaction } from '../../utils/transform-multisig-transaction';
import { ToastTooltip } from '../ToastTooltip';
import { WalletWithIcons } from '../Wallet/WalletWithIcons/WalletWithIcons';

import { TransactionNonStxToken } from './TransactionNonStxToken/TransactionNonStxToken';
import { useButtonStyles } from './useButtonStyles';

// TODO_R: [FRS]
// TODO_R: заменить @mui на makeStyles с @material-ui
export const TransactionInfo = (props: {
  transaction: IMultisigTransaction;
  memoEvent?: any;
  showLink?: boolean;
}) => {
  const { transaction, memoEvent = null, showLink = false } = props;
  const theme = useTheme();
  const internalTxType = transaction.internalTxType;
  const [network] = useNetwork();
  const classes = useButtonStyles();
  const { onMobileZone } = useBreakpoints();

  const tagsSx = {
    mt: '4px',
    display: 'flex',
    columnGap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  };
  const tagSx = {
    padding: '4px',
    background: '#3C3C44',
    alignItems: 'baseline',
    fontWeight: 400,
    fontsize: 12,
    fontSize: '90%',
    color: '#FAFAFA',
    lineHeight: '12px',
    borderRadius: '6px',
  };
  const dflexWrapSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
  const iconSx = {
    marginLeft: '4px',
  };

  const displayNotes = () => {
    if (Boolean(transaction.notes)) {
      return (
        <Tooltip
          content={transaction.notes}
          mode="click"
          position="bottom-start"
          className="max-w-[250px] text-r13 break-all"
          refElClassName="flex gap-4 items-center rounded-[24px] border bg-white-3 border-black-4 text-r12 justify-center self-start h-28 w-80 z-1 hover:bg-white-10 transition-all ml-4"
        >
          <>
            <div className="w-4 h-4 rounded-full bg-primary mr-[2px] drop-shadow-[0px_0px_8px_#FB993F]" />
            Note
            <img src={arrowIcon} alt="" />
          </>
        </Tooltip>
      );
    } else {
      return <></>;
    }
  };

  switch (internalTxType) {
    // case "stack-extend": 201224
    case 'contract-call': {
      const payload = transaction.payload as STXTransferBase;
      // @ts-ignore
      if (payload.functionName === 'stack-stx')
        return (
          <div className="flex flex-wrap">
            <div className="text-m14 text-white">Stacking STX</div>
            {displayNotes()}
          </div>
        );
      // @ts-ignore
      if (payload.functionName === 'transfer') {
        return (
          <TransactionNonStxToken
            payload={payload}
            txId={transaction.txId || ''}
            notes={displayNotes()}
          />
        );
      }
      const castedPayload = transaction.payload as STXTransferBase;

      // @ts-ignore
      const contractName = castedPayload.contractName;

      return (
        <div className="flex gap-8 items-start">
          <span>{contractName || 'Unknown contract-call'}</span>
          {Boolean(transaction.txId) && (
            <a
              href={getTxnExplorerUrl(transaction.txId || '', network)}
              target="_blank"
              rel="noreferrer"
              className={classes.linkIcon}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <LinkIcon16 />
            </a>
          )}
        </div>
      );
    }
    // case "stream-create": 201224
    case 'transfer-stx': {
      const payload = transaction.payload as STXTransferBase;

      return (
        <>
          <div className="flex items-start gap-8 mb-4 sm:mb-8">
            <div className="text-r12 sm:text-r13 md:text-r14">
              Transfer{' '}
              {(Number(payload.amount) / Math.pow(10, STX_DECIMALS))
                .toFixed(STX_DECIMALS)
                .replace(/\.?0+$/, '')}
              STX
            </div>
            {!onMobileZone && displayNotes()}
            {transaction.status === TransactionStatus.EXECUTED && transaction.txId && (
              <div className={classes.txLink}>
                <WalletWithIcons address={transaction.txId || ''} isTxn displayAddress={false} />
              </div>
            )}
            {showLink && (
              <div className={classes.txLink}>
                <WalletWithIcons address={transaction.txId || ''} isTxn displayAddress={false} />
              </div>
            )}
          </div>
          <div className="flex text-regular items-center gap-8">
            {memoEvent && memoEvent.type === 'STX20Protocol' && (
              <div className={classes.stx20}>
                +<span>{memoEvent.event.value}</span> {memoEvent.event.ticker}
              </div>
            )}
            {memoEvent && memoEvent.type === 'STXSDomain' && (
              <div className={classes.stx20}>
                +<span>{memoEvent.event.split('.')[0]}</span>.{memoEvent.event.split('.')[1]}
              </div>
            )}
            {memoEvent && memoEvent.type === 'STXSMap' && (
              <div className={classes.stx20}>
                +<span>{memoEvent.event.split('.')[0]}</span>.stxmap
              </div>
            )}
            <span className="text-r10 sm:text-r12">To:</span>
            <WalletWithIcons address={payload.recipient} />
          </div>
          {onMobileZone && displayNotes()}
        </>
      );
    }
    // case "transfer-sip-009":  201224
    default:
      const payload = transaction.payload as ContractCallBase;
      const tName = payload.contractName;
      let type = 'contract call';
      if (internalTxType === 'contract-deploy') {
        type = 'contract deploy';
      }

      return (
        <>
          <Box sx={dflexWrapSx}>
            <div className="text-m14 text-white">
              {tName} {type}
            </div>
            <Box sx={iconSx}>
              <img src={ReadOnlyIcon} alt="" />
            </Box>
          </Box>
          <Box sx={tagsSx}>
            <Box sx={tagSx}>{payload.functionName}</Box>
            <a
              href={getAccountExplorerUrl(transaction.txId || '', network)}
              target="_blank"
              rel="noreferrer"
              className={classes.linkIcon}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <LinkIcon16 />
            </a>
          </Box>
        </>
      );
  }
};

// TODO_R: [FRS]
const useStyles = makeStyles<Theme>((theme) => ({
  wrapper: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: `35% 17% 17% 10% 21%`,
    justifyItems: 'start',
    alignItems: 'center',
    borderBottom: '1px solid #323235',
    padding: '18px 32px',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '20px',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      // gridTemplateColumns: `7% 28% 16% 16% 13% 20%`,
      flexWrap: 'wrap',
      gap: '2px',
      padding: '8px 16px',
    },
    '& > div:nth-last-child(2)': {
      justifySelf: 'end',
      paddingRight: theme.spacing(2),
    },
    // '& > div:nth-last-child(1), & > div:nth-last-child(3), & > div:nth-last-child(4)': {
    //   [theme.breakpoints.down('sm')]: {
    //     display: 'none',
    //   },
    // },
    '& > div:nth-child(1)': {
      [theme.breakpoints.down('sm')]: {
        flexBasis: '60%',
      },
    },
    '& > div:nth-child(2)': {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    '& > div:nth-child(3)': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '9px',
        flexBasis: '39%',
        textAlign: 'right',
      },
    },
    '& > div:nth-child(4)': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '10px',
        marginRight: 0,
        flexBasis: '39%',
      },
    },
    '& > div:nth-child(5)': {
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-end',
        flexBasis: '60%',
      },
    },
    '& > div:nth-child(6)': {
      fontWeight: 406,
    },
    '& > div:nth-child(7)': {
      fontWeight: 407,
    },
    '& > div:nth-child(8)': {
      fontWeight: 408,
    },
  },
  typeContent: {
    alignItems: 'center',
    justifySelf: 'start',
    marginLeft: 0,
    display: 'flex',
    gap: 8,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
  txLink: {
    alignSelf: 'flex-start',
    marginTop: -4,
  },
  buttonsContent: {
    justifySelf: 'end',
    marginRight: theme.spacing(4),
  },
  confirmations: {
    background: 'rgba(253, 172, 93, 0.1)',
    borderRadius: 4,
    color: '#FDAC5D',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '20px',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      margin: '0 0 0 4px',
      marginRight: '0px !important',
      width: '12px',
      height: '12px',
    },
  },
  dateContent: {
    width: '100%',
  },
  confirmationComplete: {
    background: '#71DD5F1A',
    color: '#71DD5F',
  },
  confirmationRejected: {
    background: '#FF62621A',
    color: '#E44E45',
    '& > svg': {
      marginLeft: '20px',
      marginTop: '1px',
    },
  },
  confirmed: {
    background: 'rgba(113, 221, 95, 0.1)',
    borderRadius: 4,
    color: '#71DD5F',
    padding: theme.spacing(1, 2),
  },
  loading: {
    background: 'transparent',
    borderRadius: 4,
    color: '#FFFFFF',
    padding: theme.spacing(1, 2),
  },
  loader: {
    width: '18px',
    height: '18px',
    marginRight: '7px',
  },
}));

// TODO_R: move into HistoryTransactionsTab/components/
// TODO_R: заменить @mui на makeStyles с @material-ui
const TransactionView = (props: { transaction: IMultisigTransaction; readOnly: boolean }) => {
  const { transaction } = props;
  const classes = useStyles();
  const [executeErrorHovered, setExecuteErrorHovered] = useState(false);

  const isExecuted = transaction.status === TransactionStatus.EXECUTED;
  const isRejected = transaction.status === TransactionStatus.REJECTED;
  const isErrored = transaction.status === TransactionStatus.EXECUTION_ERRORED;
  const errorReason = transaction.errorReason;

  const desirializedTx = deserializeTransaction(transaction.rawTransaction);
  const memo = (desirializedTx as any).payload.memo;
  const memoEvent = memo && getEventFromTransferMemo(memo.content.replaceAll('\u0000', ''));
  const { onMobileZone } = useBreakpoints();

  return (
    <div className="flex overflow-hidden z-5 cursor-pointer hover:bg-white-3">
      <div className={classes.wrapper}>
        <div className={classes.typeContent}>
          <div className="hidden sm:flex items-center justify-center justify-self-start w-24 h-24 rounded-[24px] ml-0 border border-black-4 shrink-0">
            <span className="text-r12">{transaction.nonce}</span>
          </div>
          <div className={classes.txType}>
            <TransactionInfo transaction={transaction} memoEvent={memoEvent} />
          </div>
        </div>
        <div className={classes.initiatorContent}>
          <WalletWithIcons address={transaction.creator} />
        </div>
        <div className={classes.dateContent}>{formatDate(transaction.create_time)}</div>
        <div className={classes.dateContent}>
          {onMobileZone && <span className="text-regular">Fee: </span>}
          {Number(transaction.fee) / Math.pow(10, 6)}
          {' STX'}
        </div>
        {isExecuted && (
          <div className="flex items-center gap-8 justify-end">
            <div className="flex items-center gap-8 rounded-[4px] px-8 h-24 bg-success-10 ">
              <span className="text-r12 text-success">Executed</span>
            </div>
          </div>
        )}
        {isRejected && (
          <div className="flex items-center gap-8 justify-end">
            <div className="flex items-center gap-8 rounded-[4px] px-8 h-24 bg-error-10">
              <span className="text-r12 text-error">Rejected</span>
            </div>
          </div>
        )}
        {isErrored && (
          <ToastTooltip
            open={executeErrorHovered}
            message={`Reason: ${errorReason}`}
            placement="bottom"
          >
            <div
              className="flex items-center gap-8 justify-end"
              onMouseEnter={() => setExecuteErrorHovered(true)}
              onMouseLeave={() => setExecuteErrorHovered(false)}
            >
              <div className="flex items-center gap-8 rounded-[4px] px-8 h-24 bg-error-10">
                <span className="text-r12 text-error">Execution Error</span>
                <InfoIcon color="text-error" />
              </div>
            </div>
          </ToastTooltip>
        )}
      </div>
    </div>
  );
};

export default TransactionView;
